import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { pollsApi } from '../../services/api';
import hiveService from '../../services/hive';
import './PollWidget.css';

const formatTimeRemaining = (endTimeStr) => {
  if (!endTimeStr) return null;
  const end = new Date(endTimeStr).getTime();
  const now = Date.now();
  const diffMs = end - now;

  if (diffMs <= 0) return 'Final results';

  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays > 0) {
    return `${diffDays}d ${diffHours % 24}h left`;
  }
  if (diffHours > 0) {
    return `${diffHours}h ${diffMinutes % 60}m left`;
  }
  return `${diffMinutes}m left`;
};

const PollWidget = ({ author, permlink, pollData: initialPollData = null }) => {
  const { user } = useAuth();
  const [poll, setPoll] = useState(initialPollData);
  const [selectedChoice, setSelectedChoice] = useState(null);
  const [loading, setLoading] = useState(!initialPollData);
  const [voting, setVoting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (initialPollData && !poll) {
      setPoll(initialPollData);
      setLoading(false);
    }
  }, [initialPollData, poll]);

  const fetchPoll = useCallback(async () => {
    if (!author || !permlink) return;
    try {
      setLoading(true);
      setError('');
      const data = await pollsApi.getPoll(author, permlink, user?.username);
      if (data?.poll) {
        setPoll(data.poll);
        if (Array.isArray(data.poll.user_vote) && data.poll.user_vote.length > 0) {
          setSelectedChoice(data.poll.user_vote[0]);
        }
      }
    } catch (err) {
      console.error('Failed to load poll:', err);
      // If 404 and we don't have initialPollData, ignore or show minimal
    } finally {
      setLoading(false);
    }
  }, [author, permlink, user?.username]);

  useEffect(() => {
    fetchPoll();
  }, [fetchPoll]);

  if (loading && !poll) {
    return (
      <div className="poll-widget">
        <div style={{ color: 'var(--text-secondary, #888)', fontSize: '0.9rem' }}>
          Loading poll...
        </div>
      </div>
    );
  }

  if (!poll) return null;

  const hasVoted = Array.isArray(poll.user_vote) && poll.user_vote.length > 0;
  const isExpired = poll.is_expired || (poll.end_time && new Date(poll.end_time) <= new Date());
  const canVote = !isExpired && (poll.allow_vote_changes || !hasVoted);
  const showResults = hasVoted || isExpired || !poll.hide_res_until_voted;

  const handleVote = async () => {
    if (!user?.username) {
      setError('Please log in to vote');
      return;
    }
    if (!selectedChoice) {
      setError('Please select an option');
      return;
    }

    try {
      setVoting(true);
      setError('');

      // 1. Broadcast custom_json to Hive blockchain
      const targetPoll = `${author}/${permlink}`;
      const jsonPayload = {
        poll: targetPoll,
        action: 'vote',
        choices: [selectedChoice]
      };

      await hiveService.broadcastCustomJson(user.username, 'polls', jsonPayload);

      // 2. Optimistically update local database
      const voteRes = await pollsApi.vote(author, permlink, [selectedChoice]);

      if (voteRes?.success) {
        setPoll((prev) => ({
          ...prev,
          total_votes: voteRes.total_votes,
          results: voteRes.results,
          user_vote: voteRes.user_vote
        }));
      } else {
        await fetchPoll();
      }
    } catch (err) {
      console.error('Error casting vote:', err);
      setError(err?.message || 'Failed to submit vote. Please try again.');
    } finally {
      setVoting(false);
    }
  };

  return (
    <div className="poll-widget" onClick={(e) => e.stopPropagation()}>
      <div className="poll-question-header">
        <span className="poll-badge">Poll</span>
        {poll.question && <h4 className="poll-question">{poll.question}</h4>}
      </div>

      <div className="poll-choices-list">
        {(poll.results || []).map((choice) => {
          const isSelected = selectedChoice === choice.choice_num;
          const isUserVote = Array.isArray(poll.user_vote) && poll.user_vote.includes(choice.choice_num);

          return (
            <div
              key={choice.choice_num}
              className={`poll-choice-item ${canVote ? 'interactive' : ''} ${isSelected ? 'selected' : ''} ${isUserVote ? 'voted-user' : ''}`}
              onClick={() => {
                if (canVote && !voting) {
                  setSelectedChoice(choice.choice_num);
                }
              }}
            >
              {showResults && (
                <div
                  className="poll-choice-bar"
                  style={{ width: `${Math.min(choice.percentage || 0, 100)}%` }}
                />
              )}

              <div className="poll-choice-content">
                <div className="poll-choice-left">
                  {canVote && (
                    <input
                      type="radio"
                      name={`poll-${author}-${permlink}`}
                      checked={isSelected}
                      onChange={() => setSelectedChoice(choice.choice_num)}
                      className="poll-choice-input"
                      disabled={voting}
                    />
                  )}
                  <span>{choice.text}</span>
                  {isUserVote && <span className="poll-voted-check" title="Your vote">✓</span>}
                </div>

                {showResults && (
                  <div className="poll-choice-right">
                    <span className="poll-choice-percent">{choice.percentage}%</span>
                    <span className="poll-choice-votes">({choice.votes})</span>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {error && <div className="poll-error-msg">{error}</div>}

      <div className="poll-footer">
        <div className="poll-meta-left">
          <span>{poll.total_votes || 0} {poll.total_votes === 1 ? 'vote' : 'votes'}</span>
          <span>•</span>
          <span>{formatTimeRemaining(poll.end_time)}</span>
        </div>

        {canVote && (
          <button
            type="button"
            className="poll-vote-btn"
            onClick={handleVote}
            disabled={voting || !selectedChoice}
          >
            {voting ? 'Voting...' : (hasVoted ? 'Change vote' : 'Vote')}
          </button>
        )}
      </div>
    </div>
  );
};

export default PollWidget;
